import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNotification } from '../../hooks/NotificationContext';
import {
  getUserMappings,
  manualMapUser,
  syncJiraUsers,
} from '../../modules/jira';
import Modal from '../elements/Modal';
import { Sync } from '@mui/icons-material';

const NONE_VALUE = 'none';

const JiraUserMappingTableModal = ({
  project = null,
  open,
  handleClose,
  isAdmin,
  currentProjectId,
  isAddUserFlow = false,
}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const [localMappings, setLocalMappings] = useState([]);
  const projectId = project ? project.id : currentProjectId;

  const { data: mappingsData, isLoading } = useQuery({
    queryKey: ['jira-mappings', projectId],
    queryFn: () => getUserMappings(projectId),
    enabled: !!projectId && open,
    refetchOnMount: true,
  });

  const initMappings = (data) => {
    if (!data?.mapped) return;
    setLocalMappings(
      data.mapped.map((m) => ({
        ...m,
        jira_account_id: m.jira_account_id || NONE_VALUE,
        jira_display_name: m.jira_display_name || '',
        jira_email: m.jira_email || '',
      }))
    );
  };

  useEffect(() => {
    initMappings(mappingsData);
  }, [mappingsData]);


  const allJiraUsers = useMemo(() => {
    if (!mappingsData?.jira_users) return [];

    const map = new Map();

    mappingsData.jira_users.forEach((j) => {
      if (!map.has(j.jira_account_id)) {
        map.set(j.jira_account_id, {
          jiraAccountId: j.jira_account_id,
          jiraDisplayName: j.jira_display_name || 'Unknown User',
          jiraEmail: j.jira_email || '',
        });
      }
    });

    return Array.from(map.values());
  }, [mappingsData]);


  const syncMutation = useMutation({
    mutationFn: () => syncJiraUsers(projectId),
    onSuccess: () => {
      addNotification('success', 'Jira users synced successfully');
      queryClient.invalidateQueries({
        queryKey: ['jira-mappings', projectId],
      });
    },
    onError: () => addNotification('error', 'Sync failed'),
  });


  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!mappingsData?.mapped) return;

      const changed = localMappings.filter((m) => {
        const original = mappingsData.mapped.find(
          (o) => o.user_id === m.user_id
        );
        const originalId = original?.jira_account_id || NONE_VALUE;
        return originalId !== m.jira_account_id;
      });

      if (!changed.length) return;

      await manualMapUser(projectId, {
        mappings: changed.map((m) => ({
          userId: m.user_id,
          jiraAccountId: m.jira_account_id === NONE_VALUE ? null : m.jira_account_id,
          jiraDisplayName: m.jira_account_id === NONE_VALUE ? null : m.jira_display_name || null,
          jiraEmail: m.jira_account_id === NONE_VALUE ? null : m.jira_email || null,
        })),
      });
    },
    onSuccess: () => {
      addNotification('success', 'Mappings saved successfully');
      queryClient.invalidateQueries({
        queryKey: ['jira-mappings', projectId],
      });
      handleClose();
    },
    onError: () => {
      addNotification('error', 'Failed to save mappings');
    },
  });


  const handleChange = (userId, selectedAccountId) => {
    const selectedUser = allJiraUsers.find(
      (u) => u.jiraAccountId === selectedAccountId
    );

    setLocalMappings((prev) =>
      prev.map((m) =>
        m.user_id === userId
          ? {
            ...m,
            jira_account_id: selectedUser?.jiraAccountId || NONE_VALUE,
            jira_display_name: selectedUser?.jiraDisplayName || '',
            jira_email: selectedUser?.jiraEmail || '',
          }
          : m
      )
    );
  };

  const handleCancel = () => {
    initMappings(mappingsData);
    handleClose();
  };

  const hasChanges = useMemo(() => {
    if (!mappingsData?.mapped) return false;

    return localMappings.some((m) => {
      const original = mappingsData.mapped.find(
        (o) => o.user_id === m.user_id
      );
      const originalId = original?.jira_account_id || NONE_VALUE;
      return originalId !== m.jira_account_id;
    });
  }, [localMappings, mappingsData]);

  const selectedJiraUsers = useMemo(() => {
    return new Set(
      localMappings
        .map((m) => m.jira_account_id)
        .filter((id) => id && id !== NONE_VALUE)
    );
  }, [localMappings]);

  return (
    <Modal
      open={open}
      handleClose={handleCancel}
      width="md"
      modalTitle={`Jira Mapping ${isAddUserFlow && project?.jiraCloudId ? `- ${project?.name || ''}` : ''}`}
      action={() => (
        <>
          <Button variant="outlined"
            color="secondary"
            onClick={handleCancel}
            disabled={saveMutation.isPending}
            title="Cancel">Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={saveMutation.isPending}
            onClick={() => (hasChanges ? saveMutation.mutate() : handleClose())}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      )}
    >
      {isLoading ? (
        <Box textAlign="center" py={4}>
          <CircularProgress />
          <Typography mt={2}>Fetching user details...</Typography>
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="outlined"
              onClick={() => syncMutation.mutate()}
              disabled={!isAdmin || syncMutation.isPending}
              startIcon={<Sync />}
            >
              {syncMutation.isPending ? 'Syncing...' : 'Sync'}
            </Button>
          </Box>
          <Box
            component="table"
            width="100%"
            sx={{ borderCollapse: 'collapse' }}
          >
            <thead>
              <tr>
                <th align="left" width="50%">Gainss User</th>
                <th />
                <th align="left" width="50%">Jira User</th>
              </tr>
            </thead>
            <tbody>
              {!localMappings.length && (
                <tr>
                  <td colSpan={3} align="center" style={{ padding: 20 }}>
                    No users found for this project.
                  </td>
                </tr>
              )}

              {localMappings.map((row) => (
                <tr key={row.user_id}>
                  <td style={{ padding: 8 }}>
                    {row.gainss_user?.first_name}{' '}
                    {row.gainss_user?.last_name} (
                    {row.gainss_user?.email})
                  </td>
                  <td align="center">→</td>
                  <td style={{ padding: 8 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.jira_account_id}
                        onChange={(e) =>
                          handleChange(row.user_id, e.target.value)
                        }
                        renderValue={(selected) => {
                          if (selected === NONE_VALUE) {
                            return (
                              <span style={{ color: 'text.disabled', opacity: 0.5 }}>
                                None
                              </span>
                            );
                          }
                          const user = allJiraUsers.find(
                            (u) => u.jiraAccountId === selected
                          );
                          return user?.jiraDisplayName || selected;
                        }}
                      >
                        <MenuItem value={NONE_VALUE} sx={{ color: 'text.disabled' }}>
                          None
                        </MenuItem>

                        {allJiraUsers.map((j) => {
                          const isSelectedElsewhere =
                            selectedJiraUsers.has(j.jiraAccountId) &&
                            row.jira_account_id !== j.jiraAccountId;

                          return (
                            <MenuItem
                              key={j.jiraAccountId}
                              value={j.jiraAccountId}
                              disabled={isSelectedElsewhere}
                            >
                              <span style={{ textTransform: 'none' }}>{j.jiraDisplayName}</span>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </>
      )}
    </Modal>
  );
};

export default JiraUserMappingTableModal;